import { sum } from "lodash";
import { Status } from "../../graphql/generated";
import { getEffectiveShipmentIds } from "../shipment/getEffectiveShipmentIds";
import {
  isDropoffLocation,
  isPickupLocation,
} from "../location/isPickupLocation";
import { isSegmentLoaded } from "./trailerLoadState";
import {
  MileageSummary,
  ShipmentForMileage,
  TripForMileage,
  TripShipmentLocationForMileage,
} from "./types";

type OrderLocationBounds = {
  firstPickupIndex: number;
  lastDropoffIndex: number;
};

function getOrderLocationBounds(
  shipmentLocations: TripShipmentLocationForMileage[],
  shipmentId: string,
): OrderLocationBounds | null {
  const orderIndices = shipmentLocations
    .map((location, index) =>
      getEffectiveShipmentIds(location).includes(shipmentId) ? index : -1,
    )
    .filter((index) => index >= 0);

  if (orderIndices.length === 0) {
    return null;
  }

  const firstPickupIndex = orderIndices.find((index) =>
    isPickupLocation(shipmentLocations[index]),
  );
  const lastDropoffIndex = [...orderIndices]
    .reverse()
    .find((index) => isDropoffLocation(shipmentLocations[index]));

  if (firstPickupIndex === undefined || lastDropoffIndex === undefined) {
    return null;
  }

  return { firstPickupIndex, lastDropoffIndex };
}

function sumDistanceToInRange(
  shipmentLocations: TripShipmentLocationForMileage[],
  startIndex: number,
  endIndex: number,
): number {
  return sum(
    shipmentLocations
      .slice(startIndex, endIndex + 1)
      .map((location) => location.distanceTo ?? 0),
  );
}

function sumLoadedSegmentsInRange(
  shipmentLocations: TripShipmentLocationForMileage[],
  startIndex: number,
  endIndex: number,
): number {
  return sum(
    shipmentLocations
      .slice(startIndex, endIndex + 1)
      .map((location, offset) => {
        const index = startIndex + offset;

        if (!isSegmentLoaded(shipmentLocations, index)) {
          return 0;
        }

        return location.distanceTo ?? 0;
      }),
  );
}

function getUnassignedOrderMileage(
  shipment: ShipmentForMileage,
): MileageSummary {
  return {
    routeMiles: shipment.route?.routeDistance ?? null,
    loadedMiles: shipment.route?.predictedLoadedDistance ?? null,
    emptyMiles: null,
    actualMiles: null,
  };
}

function getCompletedSingleShipmentOrderMileage(
  shipment: ShipmentForMileage,
  trip: TripForMileage,
): MileageSummary {
  const plannedEmptyMiles =
    (trip.distanceToStart ?? 0) + (trip.distanceToEnd ?? 0);

  return {
    routeMiles: trip.routeDistance ?? shipment.route?.routeDistance ?? null,
    loadedMiles:
      trip.loadedDistance ?? trip.predictedLoadedDistance ?? null,
    emptyMiles: trip.deadheadDistance ?? plannedEmptyMiles,
    actualMiles: trip.totalDistance ?? null,
  };
}

export function getOrderMileageSummary(
  shipment: ShipmentForMileage,
  trip?: TripForMileage | null,
): MileageSummary {
  if (!trip || !shipment.tripId) {
    return getUnassignedOrderMileage(shipment);
  }

  const isComplete = trip.status === Status.Complete;
  const isSingleShipmentTrip = (trip.shipments?.length ?? 0) === 1;

  if (isComplete && isSingleShipmentTrip) {
    return getCompletedSingleShipmentOrderMileage(shipment, trip);
  }

  const bounds = getOrderLocationBounds(
    trip.shipmentLocations,
    shipment._id,
  );

  if (!bounds) {
    return {
      routeMiles: shipment.route?.routeDistance ?? null,
      loadedMiles: shipment.route?.predictedLoadedDistance ?? null,
      emptyMiles: 0,
      actualMiles: null,
    };
  }

  const orderRouteMiles = sumDistanceToInRange(
    trip.shipmentLocations,
    bounds.firstPickupIndex,
    bounds.lastDropoffIndex,
  );
  const orderLoadedMiles = isComplete
    ? sumLoadedSegmentsInRange(
        trip.shipmentLocations,
        bounds.firstPickupIndex,
        bounds.lastDropoffIndex,
      )
    : orderRouteMiles;

  return {
    routeMiles: orderRouteMiles || shipment.route?.routeDistance || null,
    loadedMiles:
      orderLoadedMiles || shipment.route?.predictedLoadedDistance || null,
    emptyMiles: 0,
    actualMiles: null,
  };
}
